import { API_URL, apiGet, apiPost } from './apiClient';

export interface AdCreatorGeneratePayloadInput {
  adGroupId?: string;
  keywords: string[];
  persona: string;
  scene?: string;
  specialization?: string;
  celebrityNames?: string[];
  activityNames?: string[];
  metaPostUrl?: string;
  metaPostText?: string;
  pmaxPlanId?: string;
  pmaxContext?: {
    entryPoint?: '媽媽族群' | '瘦身減肥' | '怕熱女孩' | '腰痠背痛' | '名媛貴婦';
    scenarioKey?: string;
    landingPageId?: string;
    searchThemes?: string[];
    audienceSignals?: string[];
  };
}

export interface AdCreatorGenerateResponse<TGenerated = unknown, TMetadata = unknown> {
  success: boolean;
  generated?: TGenerated;
  metadata?: TMetadata;
  pmaxPlan?: unknown;
  error?: string;
}

export type AssetFamily = 'H1' | 'H2' | 'H3' | 'D';
export type AssetStatus = 'pending' | 'generating' | 'rejected' | 'completed' | 'stale';
export type GenerationStatus = 'queued' | 'running' | 'completed' | 'system_fault';
export type GenerationPhase = 'queued' | 'strategy_analysis' | 'asset_generation' | 'history_persistence' | 'completed' | 'system_fault';
export type V2ProgressStepId = 'input_prep' | 'strategy' | 'creative' | 'judge' | 'rewrite' | 'finalize';
export type V2ProgressStatus = 'running' | 'completed' | 'skipped';

export interface V2ProgressUpdate {
  stepId: V2ProgressStepId;
  label: string;
  status: V2ProgressStatus;
  index: number;
  total: number;
  detail?: string;
}

export interface GenerationAssetSnapshot {
  assetId: string;
  family: AssetFamily;
  index: number;
  status: AssetStatus;
  attemptIndex: number;
  currentText?: string;
  charCount?: number;
  rejectionReasons: string[];
  forbiddenNormalizedCopies: string[];
  forbiddenLeadPatterns: string[];
  forbiddenSemanticClusters: string[];
  slotContract: string;
  dependencies: string[];
}

export interface GenerationState<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown> {
  generationId: string;
  workflowInstanceId: string;
  mode: 'rsa' | 'pmax';
  status: GenerationStatus;
  phase: GenerationPhase;
  assets: GenerationAssetSnapshot[];
  metadata?: TMetadata;
  generated?: TGenerated;
  pmaxPlan?: TPmaxPlan;
  progress?: V2ProgressUpdate;
  error?: string;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface BrandAssetUsage {
  assetId: string;
  family: 'H1' | 'H2' | 'H3' | 'D';
  index: number;
  variant?: string;
  slotIntent: string;
  sourceType: 'core_technology' | 'trust_signal' | 'unique_service' | 'offer' | 'celebrity' | 'activity';
  sourceId: string;
  sourceLabel: string;
}

export interface GenerationEventPayload<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown> {
  seq: number;
  type:
    | 'generation_started'
    | 'asset_started'
    | 'asset_rejected'
    | 'asset_regenerated'
    | 'asset_completed'
    | 'set_validation_failed'
    | 'dependency_invalidated'
    | 'progress_updated'
    | 'completed'
    | 'system_fault';
  timestamp: string;
  status?: GenerationStatus;
  phase?: GenerationPhase;
  assetId?: string;
  family?: AssetFamily;
  index?: number;
  attemptIndex?: number;
  reasons?: string[];
  dependsOn?: string[];
  text?: string;
  asset?: GenerationAssetSnapshot;
  generated?: TGenerated;
  metadata?: TMetadata;
  progress?: V2ProgressUpdate;
  pmaxPlan?: TPmaxPlan;
  error?: string;
}

export interface AdCreatorGenerationStartResponse<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown> {
  success: boolean;
  generationId: string;
  status: GenerationStatus;
  state: GenerationState<TGenerated, TMetadata, TPmaxPlan>;
  generated?: TGenerated;
  metadata?: TMetadata;
  pmaxPlan?: TPmaxPlan;
  streamUrl: string;
  resultUrl: string;
  error?: string;
}

export interface AdCreatorGenerationResultResponse<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown> {
  success: boolean;
  generationId: string;
  status: GenerationStatus;
  state: GenerationState<TGenerated, TMetadata, TPmaxPlan>;
  generated?: TGenerated;
  metadata?: TMetadata;
  pmaxPlan?: TPmaxPlan;
  error?: string;
}

export interface PmaxPlanPayload {
  keywords?: string[];
  persona?: string;
  scene?: string;
  specialization?: string;
  metaPostText?: string;
  pmaxPlanId?: string;
  pmaxContext?: AdCreatorGeneratePayloadInput['pmaxContext'];
}

export interface PmaxPlanResponse {
  success: boolean;
  plan?: {
    pmaxPlanId: string;
    availableScenarios: Array<{
      key: string;
      label: string;
      scene: string;
    }>;
    candidateLandingPages: Array<{
      id: string;
      title: string;
      url: string;
      heroAngle: string;
      matchScore: number;
    }>;
    entryPointPlan: {
      entryPoint: '媽媽族群' | '瘦身減肥' | '怕熱女孩' | '腰痠背痛' | '名媛貴婦';
      persona: string;
      plannerSummary: string;
      recommendedSpecialization: string;
      planningReason: string;
    };
    scenarioPlan: {
      entryPoint: '媽媽族群' | '瘦身減肥' | '怕熱女孩' | '腰痠背痛' | '名媛貴婦';
      scenarioKey: string;
      scenarioLabel: string;
      customerState: string;
      coreNeed: string;
      objection: string;
      openingNarrative: string;
    };
    landingPagePlan: {
      landingPageId: string;
      url: string;
      title: string;
      matchReason: string;
      heroAngle: string;
      supportProofs: string[];
      matchStatus: 'matched' | 'weak_match' | 'mismatch';
    };
    assetGroupPlan: {
      themeName: string;
      visualDirection: string;
      proofPool: string[];
      copyOpeningAxis: string;
      forbiddenMixes: string[];
    };
    searchSignalPlan: {
      searchThemes: string[];
      audienceSignals: string[];
      translationNotes: string[];
    };
    copyPlan: {
      shortHeadlines: string[];
      longHeadlines: string[];
      descriptions: string[];
      strategySnapshot: {
        persona: string;
        scene: string;
        specialization: string;
        openingNarrative: string;
      };
    };
    generationContext: {
      planId: string;
      entryPoint: '媽媽族群' | '瘦身減肥' | '怕熱女孩' | '腰痠背痛' | '名媛貴婦';
      persona: string;
      scene: string;
      specialization: string;
      scenarioKey: string;
      landingPageId: string;
      keywords: string[];
      searchThemes: string[];
      audienceSignals: string[];
    };
  };
  error?: string;
}

export interface AdCreatorMarkUsagePayload {
  generationAssetId: number;
  usedText?: string;
  wasModified?: boolean;
  googleAdsAssetId?: string;
  adGroupId?: string;
  campaignId?: string;
}

export interface AdCreatorFeedbackPayload {
  generationAssetId: number;
  adGroupId: string;
  assetText: string;
  fieldType: 'HEADLINE' | 'DESCRIPTION';
  impressions?: number;
  clicks?: number;
  conversions?: number;
  costMicros?: number;
  performanceLabel?: 'GOOD' | 'AVERAGE' | 'POOR' | 'LEARNING';
  notes?: string;
}

export interface AdCreatorHistoryFilters {
  customerId?: string;
  adGroupId?: string;
  persona?: string;
  limit?: string | number;
  offset?: string | number;
}

export interface AdCreatorSyncGoogleAdsPayload {
  startDate?: string;
  endDate?: string;
  adGroupIds?: string[];
  matchThreshold?: number;
}

export interface AdCreatorSyncStatusResponse {
  success: boolean;
  lastSync?: string | null;
  needsSync?: boolean;
  error?: string;
}

export interface AdCreatorSyncGoogleAdsResponse {
  success: boolean;
  result?: {
    syncedAssets: number;
    matchedAssets: number;
    newFeedbacks: number;
    dateRange?: {
      startDate?: string;
      endDate?: string;
    };
    errors?: string[];
  };
  error?: string;
}

async function postJson<TResponse>(path: string, body: unknown): Promise<TResponse> {
  const response = await apiPost(path, body);
  return response.json() as Promise<TResponse>;
}

async function getJson<TResponse>(path: string, searchParams?: URLSearchParams): Promise<TResponse> {
  const suffix = searchParams && searchParams.toString() ? `?${searchParams.toString()}` : '';
  const response = await apiGet(`${path}${suffix}`);
  return response.json() as Promise<TResponse>;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function normalizeNamedList(values?: string[]): string[] | undefined {
  if (!values || values.length === 0) return undefined;
  const normalized = Array.from(
    new Set(values.map((value) => value.trim()).filter(Boolean))
  );
  return normalized.length > 0 ? normalized : undefined;
}

export function buildAdCreatorGeneratePayload(input: AdCreatorGeneratePayloadInput) {
  return {
    ...(input.adGroupId ? { adGroupId: input.adGroupId } : {}),
    keywords: input.keywords,
    persona: input.persona,
    ...(input.scene ? { scene: input.scene } : {}),
    ...(input.specialization ? { specialization: input.specialization } : {}),
    ...(normalizeNamedList(input.celebrityNames) ? { celebrityNames: normalizeNamedList(input.celebrityNames) } : {}),
    ...(normalizeNamedList(input.activityNames) ? { activityNames: normalizeNamedList(input.activityNames) } : {}),
    ...(input.metaPostUrl?.trim() ? { metaPostUrl: input.metaPostUrl.trim() } : {}),
    ...(input.metaPostText?.trim() ? { metaPostText: input.metaPostText.trim() } : {}),
    ...(input.pmaxPlanId ? { pmaxPlanId: input.pmaxPlanId } : {}),
    ...(input.pmaxContext ? { pmaxContext: input.pmaxContext } : {}),
  };
}

export async function postAdCreatorGenerateFull<TGenerated = unknown, TMetadata = unknown>(
  input: AdCreatorGeneratePayloadInput
): Promise<AdCreatorGenerationStartResponse<TGenerated, TMetadata>> {
  return postJson<AdCreatorGenerationStartResponse<TGenerated, TMetadata>>(
    '/api/ad-creator/generate-full',
    buildAdCreatorGeneratePayload(input)
  );
}

export async function postAdCreatorGenerateFullV2<TGenerated = unknown, TMetadata = unknown>(
  input: AdCreatorGeneratePayloadInput
): Promise<AdCreatorGenerationStartResponse<TGenerated, TMetadata>> {
  return postJson<AdCreatorGenerationStartResponse<TGenerated, TMetadata>>(
    '/api/v2/ad-creator/generate-full',
    buildAdCreatorGeneratePayload(input)
  );
}

export async function getAdCreatorGenerationResult<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown>(
  generationId: string
): Promise<AdCreatorGenerationResultResponse<TGenerated, TMetadata, TPmaxPlan>> {
  return getJson<AdCreatorGenerationResultResponse<TGenerated, TMetadata, TPmaxPlan>>(
    `/api/ad-creator/generate-full/result/${encodeURIComponent(generationId)}`
  );
}

export async function getAdCreatorGenerationResultV2<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown>(
  generationId: string
): Promise<AdCreatorGenerationResultResponse<TGenerated, TMetadata, TPmaxPlan>> {
  return getJson<AdCreatorGenerationResultResponse<TGenerated, TMetadata, TPmaxPlan>>(
    `/api/v2/ad-creator/generate-full/result/${encodeURIComponent(generationId)}`
  );
}

export async function waitForAdCreatorGenerationResult<TGenerated = unknown, TMetadata = unknown, TPmaxPlan = unknown>(
  generationId: string,
  pollIntervalMs = 1000
): Promise<AdCreatorGenerationResultResponse<TGenerated, TMetadata, TPmaxPlan>> {
  for (;;) {
    const result = await getAdCreatorGenerationResult<TGenerated, TMetadata, TPmaxPlan>(generationId);
    if (result.status === 'completed' || result.status === 'system_fault') {
      return result;
    }
    await delay(pollIntervalMs);
  }
}

export function createAdCreatorGenerationEventSource(generationId: string): EventSource {
  const token = localStorage.getItem('auth_token');
  const url = new URL(`${API_URL}/api/ad-creator/generate-full/stream/${encodeURIComponent(generationId)}`);
  if (token) {
    url.searchParams.set('access_token', token);
  }
  return new EventSource(url.toString());
}

export function createAdCreatorGenerationEventSourceV2(generationId: string): EventSource {
  const token = localStorage.getItem('auth_token');
  const url = new URL(`${API_URL}/api/v2/ad-creator/generate-full/stream/${encodeURIComponent(generationId)}`);
  if (token) {
    url.searchParams.set('access_token', token);
  }
  return new EventSource(url.toString());
}

export async function postAdCreatorPmaxPlan(
  payload: PmaxPlanPayload
): Promise<PmaxPlanResponse> {
  return postJson('/api/ad-creator/pmax-plan', payload);
}

export async function postAdCreatorMarkUsage(
  payload: AdCreatorMarkUsagePayload
): Promise<{ success: boolean; message?: string; error?: string }> {
  return postJson('/api/ad-creator/mark-usage', payload);
}

export async function postAdCreatorFeedback(
  payload: AdCreatorFeedbackPayload
): Promise<{ success: boolean; message?: string; error?: string }> {
  return postJson('/api/ad-creator/feedback', payload);
}

export async function postAdCreatorValidate(
  payload: {
    headlines?: string[];
    descriptions?: string[];
    specialization?: string;
    celebrityNames?: string[];
    tvShowNames?: string[];
    activityNames?: string[];
    headlineTypes?: Array<'H1' | 'H2' | 'H3'>;
  }
): Promise<{
  success: boolean;
  validation?: {
    headlines: Array<{ text: string; isValid: boolean; issues: string[] }>;
    descriptions: Array<{ text: string; isValid: boolean; issues: string[] }>;
  };
  summary?: {
    headlinesValid: number;
    headlinesTotal: number;
    descriptionsValid: number;
    descriptionsTotal: number;
  };
  error?: string;
}> {
  return postJson('/api/ad-creator/validate', payload);
}

export async function getAdCreatorHistory<TRecord = unknown>(
  filters: AdCreatorHistoryFilters = {}
): Promise<{ success: boolean; history?: TRecord[]; error?: string }> {
  const params = new URLSearchParams();
  if (filters.customerId) params.append('customerId', filters.customerId);
  if (filters.adGroupId) params.append('adGroupId', filters.adGroupId);
  if (filters.persona) params.append('persona', filters.persona);
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters.offset !== undefined) params.append('offset', String(filters.offset));
  return getJson('/api/ad-creator/history', params);
}

export async function getAdCreatorSyncStatus(): Promise<AdCreatorSyncStatusResponse> {
  return getJson('/api/ad-creator/sync-status');
}

export async function postAdCreatorSyncGoogleAds(
  payload: AdCreatorSyncGoogleAdsPayload = {}
): Promise<AdCreatorSyncGoogleAdsResponse> {
  return postJson('/api/ad-creator/sync-google-ads', payload);
}
